import {Link, Typography} from '@mui/material';
import {FiberManualRecord} from '@mui/icons-material';
import {TOOLTIP_SECTIONS} from './commons/constants';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import './PropertyEstimateTooltip.scss';
import {Container, Popup} from 'semantic-ui-react';
import {getConfidenceFromAvm} from 'lib/AvmUtils';
import PropertyEstimateRange from 'components/PropertyEstimateRange';

/**
 * Displays the property estimate tooltip for a property (e.g. the target property)
 * @returns {JSX.Element}
 * @todo This uses Semantic UI elements but in the redesign it should be moved over to Material
 */
const PropertyEstimateTooltip = ({ avm, isRental, disclaimer, section, onCopyToReport }) => {

  const [confidence, setConfidence] = useState(() => getConfidenceFromAvm(avm, isRental));

  useEffect(() => {
    setConfidence(getConfidenceFromAvm(avm, isRental));
  }, [avm, isRental]);

  function confidenceLevel() {
    return (
      <aside className="PropertyEstimateTooltip" data-testid="PropertyEstimateTooltip" tabIndex="0">
        <FiberManualRecord
          aria-label="confidence indicator"
          classes={{ root: `icon ${confidence.className}` }}
        />
        <Link className={`details ${confidence.className}`} component="button">
          {confidence.label + ' Confidence'}
        </Link>
        <div className="info">i</div>
      </aside>
    );
  }

  function confidenceRange() {
    return (
      <PropertyEstimateRange avm={avm} isRental={isRental} onCopyToReport={onCopyToReport}/>
    );
  }

  function isSummary() {
    return 'Summary' === section;
  }

  function notaBene() {
    const msg = isSummary()
      ? 'The Cotality valuation estimate isn\'t included on the report, to include this estimate click Copy to report.'
      : 'This estimated value is only shown to you and will not appear on the report.';

    return (
      <p>
        <strong>n.b.</strong> {msg}
      </p>
    );
  }

  return (
    <Popup on="click"
           position="bottom center"
           trigger={isSummary() ? confidenceRange() : confidenceLevel()}
           wide>
      <Container fluid>
        {isSummary() && (
          <div className="header">
            <FiberManualRecord
              aria-label="confidence indicator"
              classes={{ root: `icon ${confidence.className}` }}
            />
            <Typography classes={{root: confidence.className}}>{confidence.label} confidence</Typography>
          </div>
        )}
        <p dangerouslySetInnerHTML={{ __html: disclaimer }} />
        {notaBene()}
      </Container>
    </Popup>
  );
};

PropertyEstimateTooltip.propTypes = {
  avm: PropTypes.shape({
    estimate: PropTypes.number,
    lowEstimate: PropTypes.number,
    highEstimate: PropTypes.number,
    fsd: PropTypes.number,
    clapiConfidence: PropTypes.oneOf([
      'NONE',
      'LOW',
      'MEDIUM_LOW',
      'MEDIUM',
      'MEDIUM_HIGH',
      'HIGH',
    ]),
    confidence: PropTypes.string,
    source: PropTypes.string,
  }),
  isRental: PropTypes.bool,
  disclaimer: PropTypes.string,
  section: PropTypes.oneOf(TOOLTIP_SECTIONS),
  onCopyToReport: PropTypes.func,
};

PropertyEstimateTooltip.defaultProps = {
  avm: {
    estimate: 0,
    lowEstimate: 0,
    highEstimate: 0,
    fsd: Infinity,
  },
  section: 'Target Property',
};

export default PropertyEstimateTooltip;
